import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ProgressStep, SectionCard } from '../../../design-system';
import type { WorkflowTemplateCreateInput } from '../model';
import { WorkflowOrganizationPicker, WorkflowPersonPicker, type WorkflowOrganizationOption, type WorkflowPersonOption } from '../WorkflowReferencePickers';
import { approvalDescription } from '../models/workflowTemplatePageModel';
import { workflowTemplateIconVisual } from '../workflowTemplateIcons';
import { DynamicField } from './DynamicWorkflowField';

export function WorkflowTemplatePreview({ value, personOptions, organizationOptions, loading, error, onRetry }: {
  value: WorkflowTemplateCreateInput;
  personOptions: readonly WorkflowPersonOption[];
  organizationOptions: readonly WorkflowOrganizationOption[];
  loading?: boolean;
  error?: string;
  onRetry: () => void;
}) {
  const [samples, setSamples] = useState<Record<string, unknown>>({});
  const visual = workflowTemplateIconVisual(value.iconKey);
  return <Box component="aside" aria-label="양식 미리보기" sx={{ minWidth: 0, position: { lg: 'sticky' }, top: 16 }}>
    <SectionCard title="미리보기" help="미리보기 입력은 저장되지 않습니다." bodyPadding="standard" action={<Button size="small" onClick={() => setSamples({})}>입력 초기화</Button>}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          <Box aria-hidden data-testid={`workflow-template-preview-icon-${value.iconKey.toLowerCase()}`} sx={{ color: visual.color, display: 'grid', placeItems: 'center' }}>{visual.icon}</Box>
          <Box sx={{ minWidth: 0 }}><Typography variant="h6">{value.displayName.trim() || '양식 이름'}</Typography>{value.description?.trim() && <Typography variant="body2" color="text.secondary">{value.description.trim()}</Typography>}</Box>
        </Stack>
        {value.fields.map((field) => {
          const sample = samples[field.key];
          const onChange = (next: unknown) => setSamples((current) => ({ ...current, [field.key]: next }));
          return field.type === 'PERSON' ? <WorkflowPersonPicker key={field.key} label={field.label} required={field.required} personId={String(sample ?? '')} options={personOptions} loading={loading} error={error} onRetry={onRetry} onChange={onChange} />
            : field.type === 'ORGANIZATION' ? <WorkflowOrganizationPicker key={field.key} label={field.label} required={field.required} organizationId={String(sample ?? '')} options={organizationOptions} loading={loading} error={error} onRetry={onRetry} onChange={onChange} />
              : <DynamicField key={`${field.key}-${field.type}`} field={field} value={sample} onChange={onChange} />;
        })}
        {value.fields.some((field) => field.type === 'FILE' || field.type === 'WORKFLOW_REFERENCE') && <Typography variant="caption" color="text.secondary">파일 첨부와 다른 문서 연결은 실제 요청을 작성할 때 사용할 수 있습니다.</Typography>}
        <Stack spacing={1}>
          <Typography variant="subtitle2">요청 시 기본 결재선</Typography>
          {value.approvalSteps.map((step) => <ProgressStep key={step.order} marker={step.stageOrder} label={step.label} description={`${step.stageOrder}단계 · ${approvalDescription(step)} · ${step.requesterEditable ? '요청자가 수정 가능' : '고정 단계'}`} />)}
        </Stack>
      </Stack>
    </SectionCard>
  </Box>;
}
